// ═══════════════════════════════════════════════════════════════════════
// Town Classifier: Classify addresses as town vs suburb using Mapbox
// Properly parses TaxiCaller address format: "POI, Street (Zone, TownCode)"
// ═══════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import {
  MapPin, Loader2, Search, X, Building2, Home,
  HelpCircle, CheckCircle2, Scan, Globe, Plus, Pencil, Trash2,
} from 'lucide-react';
import type { TownClassification, ExportJobRow } from './types';

// ─── Props ──────────────────────────────────────────────────────────

interface TownClassifierProps {
  open: boolean;
  onClose: () => void;
  rows: ExportJobRow[];
  classifications: TownClassification[];
  onClassificationsChanged: () => void;
}

// ─── Address Parsing ────────────────────────────────────────────────

interface ParsedAddress {
  raw: string;
  poi: string;
  street: string;
  zoneCode: string;
  townCode: string;
}

/** Known country strings to filter out when extracting town from comma-separated addresses */
const COUNTRY_PATTERNS = /^(new\s*zealand|nz|australia|au)$/i;

/**
 * Parse a TaxiCaller address into its components.
 * 
 * Supports TWO formats:
 * 
 * 1. TaxiCaller zone format: "POI, Street (ZoneCode, TownCode)"
 *    e.g. "Health Hub, Main Street (404, PN)"
 *    → townCode = "PN"
 * 
 * 2. Plain comma-separated: "Street, Town, Country"
 *    e.g. "7A Victoria Street, Foxton, New Zealand"
 *    → townCode = "Foxton"
 */
function parseTaxiCallerAddress(raw: string): ParsedAddress {
  if (!raw) return { raw, poi: '', street: '', zoneCode: '', townCode: '' };

  // ── Format 1: TaxiCaller zone format with parenthetical (ZoneCode, TownCode) ──
  // Match the LAST (...) that contains a comma (zone, town pattern)
  const lastParenMatch = raw.match(/\(([^()]+)\)\s*$/);
  if (lastParenMatch) {
    const insideParen = lastParenMatch[1];
    const parenParts = insideParen.split(',').map(p => p.trim());

    let zoneCode = '';
    let townCode = '';

    if (parenParts.length >= 2) {
      zoneCode = parenParts[0];
      townCode = parenParts.slice(1).join(',').trim();
    } else if (parenParts.length === 1) {
      townCode = parenParts[0];
    }

    // Remove the last parenthetical from the address
    const addressWithoutZone = raw.substring(0, raw.lastIndexOf('(' + lastParenMatch[1] + ')')).trim();

    // Split remaining by first comma: POI, Street
    let poi = '';
    let street = '';
    const commaIdx = addressWithoutZone.indexOf(',');
    if (commaIdx > 0) {
      poi = addressWithoutZone.substring(0, commaIdx).trim();
      street = addressWithoutZone.substring(commaIdx + 1).trim();
    } else {
      street = addressWithoutZone.trim();
    }

    // Clean up street - remove any remaining parenthetical content
    street = street.replace(/\s*\([^)]*\)\s*/g, '').trim();

    return { raw, poi, street, zoneCode, townCode };
  }

  // ── Format 2: Plain comma-separated address ──
  // e.g. "7A Victoria Street, Foxton, New Zealand"
  // or   "Crest Hospital Carroll Street, Palmerston North"
  const parts = raw.split(',').map(p => p.trim()).filter(Boolean);

  // Filter out country names (New Zealand, NZ, etc.)
  const nonCountryParts = parts.filter(p => !COUNTRY_PATTERNS.test(p));

  if (nonCountryParts.length >= 2) {
    // Last non-country part is the town, everything before is street/POI
    const townCode = nonCountryParts[nonCountryParts.length - 1];
    const streetParts = nonCountryParts.slice(0, -1);
    const street = streetParts.join(', ');

    return {
      raw,
      poi: '',
      street,
      zoneCode: '',
      townCode,
    };
  }

  if (nonCountryParts.length === 1) {
    // Single part - could be just a town name or just a street
    return {
      raw,
      poi: '',
      street: nonCountryParts[0],
      zoneCode: '',
      townCode: nonCountryParts[0],
    };
  }

  // Fallback: no useful parts found
  return { raw, poi: '', street: raw, zoneCode: '', townCode: '' };
}

/**
 * Clean an address for Mapbox geocoding:
 * Remove zone codes, keep meaningful parts, add "New Zealand"
 */
function cleanAddressForGeocoding(parsed: ParsedAddress): string {
  const parts: string[] = [];

  // Use street if available
  if (parsed.street) {
    parts.push(parsed.street);
  } else if (parsed.poi) {
    parts.push(parsed.poi);
  }

  // Add the town code if it looks like a real name (not just an abbreviation)
  if (parsed.townCode && parsed.townCode.length > 3) {
    parts.push(parsed.townCode);
  }

  parts.push('New Zealand');
  return parts.join(', ');
}


// ─── Location Data Structure ────────────────────────────────────────

interface ExtractedLocation {
  /** The town code as extracted from addresses (e.g. "PN", "West", "Mils") */
  townCode: string;
  /** Zone codes associated with this town code */
  zoneCodes: Set<string>;
  /** Number of address occurrences */
  count: number;
  /** Sample raw addresses for this town code */
  sampleAddresses: string[];
  /** Sample parsed addresses */
  sampleParsed: ParsedAddress[];
  /** Existing classification from DB (if any) */
  classification: TownClassification | null;
}

// ─── Component ──────────────────────────────────────────────────────

const TownClassifier: React.FC<TownClassifierProps> = ({
  open, onClose, rows, classifications, onClassificationsChanged,
}) => {
  const [classifying, setClassifying] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'town' | 'suburb' | 'unknown' | 'unclassified'>('all');
  const [saving, setSaving] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDisplayAs, setEditDisplayAs] = useState('');
  const [editParentTown, setEditParentTown] = useState('');
  const [addingManual, setAddingManual] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualType, setManualType] = useState<'town' | 'suburb'>('town');
  const [manualDisplayAs, setManualDisplayAs] = useState('');
  const [manualParentTown, setManualParentTown] = useState('');

  // Build a lookup map from classifications
  const classificationMap = useMemo(() => {
    const map = new Map<string, TownClassification>();
    for (const c of classifications) {
      map.set(c.location_name.toLowerCase(), c);
    }
    return map;
  }, [classifications]);

  // Extract unique town codes from current data
  const uniqueLocations = useMemo(() => {
    const locationMap = new Map<string, ExtractedLocation>();

    for (const row of rows) {
      for (const field of ['pick_up_location', 'drop_off_location'] as const) {
        const addr = row[field];
        if (!addr) continue;

        const parsed = parseTaxiCallerAddress(addr);
        if (!parsed.townCode || parsed.townCode.length < 1) continue;

        const key = parsed.townCode.toLowerCase().trim();
        const existing = locationMap.get(key);

        if (existing) {
          existing.count++;
          if (parsed.zoneCode) existing.zoneCodes.add(parsed.zoneCode);
          if (existing.sampleAddresses.length < 4 && !existing.sampleAddresses.includes(addr)) {
            existing.sampleAddresses.push(addr);
            existing.sampleParsed.push(parsed);
          }
        } else {
          locationMap.set(key, {
            townCode: parsed.townCode,
            zoneCodes: new Set(parsed.zoneCode ? [parsed.zoneCode] : []),
            count: 1,
            sampleAddresses: [addr],
            sampleParsed: [parsed],
            classification: classificationMap.get(key) || null,
          });
        }
      }
    }

    return Array.from(locationMap.entries())
      .map(([key, data]) => ({ key, ...data }))
      .sort((a, b) => b.count - a.count);
  }, [rows, classificationMap]);

  // Also include classifications that exist in DB but not in current data
  const allLocations = useMemo(() => {
    const dataKeys = new Set(uniqueLocations.map(l => l.key));
    const dbOnly: typeof uniqueLocations = [];

    for (const c of classifications) {
      const key = c.location_name.toLowerCase();
      if (!dataKeys.has(key)) {
        dbOnly.push({
          key,
          townCode: c.location_name,
          zoneCodes: new Set<string>(),
          count: 0,
          sampleAddresses: c.source_addresses || [],
          sampleParsed: [],
          classification: c,
        });
      }
    }

    return [...uniqueLocations, ...dbOnly];
  }, [uniqueLocations, classifications]);

  // Filter locations
  const filteredLocations = useMemo(() => {
    return allLocations.filter(loc => {
      if (filterType === 'unclassified' && loc.classification !== null) return false;
      if (filterType === 'town' && loc.classification?.location_type !== 'town') return false;
      if (filterType === 'suburb' && loc.classification?.location_type !== 'suburb') return false;
      if (filterType === 'unknown' && loc.classification?.location_type !== 'unknown') return false;

      if (searchText.trim()) {
        const s = searchText.toLowerCase();
        return loc.townCode.toLowerCase().includes(s) ||
          (loc.classification?.display_as || '').toLowerCase().includes(s) ||
          (loc.classification?.parent_town || '').toLowerCase().includes(s) ||
          Array.from(loc.zoneCodes).some(z => z.includes(s));
      }
      return true;
    });
  }, [allLocations, filterType, searchText]);

  // Stats
  const stats = useMemo(() => {
    const total = allLocations.length;
    const towns = allLocations.filter(l => l.classification?.location_type === 'town').length;
    const suburbs = allLocations.filter(l => l.classification?.location_type === 'suburb').length;
    const unknown = allLocations.filter(l => l.classification?.location_type === 'unknown').length;
    const unclassified = allLocations.filter(l => !l.classification).length;
    return { total, towns, suburbs, unknown, unclassified };
  }, [allLocations]);

  // ─── Classify via Mapbox ────────────────────────────────────────
  const handleClassifyAll = useCallback(async () => {
    const unclassified = allLocations.filter(l => !l.classification);
    if (unclassified.length === 0) {
      toast({ title: 'All Classified', description: 'All locations are already classified.' });
      return;
    }

    setClassifying(true);
    let successCount = 0;
    let failCount = 0;

    // Process in batches of 10
    const batches: typeof unclassified[] = [];
    for (let i = 0; i < unclassified.length; i += 10) {
      batches.push(unclassified.slice(i, i + 10));
    }

    for (const batch of batches) {
      try {
        // Build cleaned addresses for geocoding
        const addresses = batch.map(loc => {
          if (loc.sampleParsed.length > 0) {
            return cleanAddressForGeocoding(loc.sampleParsed[0]);
          }
          // Fallback: use sample address with basic cleaning
          const sample = loc.sampleAddresses[0] || loc.townCode;
          return sample.replace(/\s*\([^)]*\)/g, '').trim() + ', New Zealand';
        });

        const { data, error } = await supabase.functions.invoke('mapbox-geocode', {
          body: { action: 'classify', addresses },
        });

        if (error) {
          console.error('Mapbox classify error:', error);
          failCount += batch.length;
          continue;
        }

        const results = data?.results || [];

        for (let i = 0; i < batch.length; i++) {
          const loc = batch[i];
          const result = results[i];

          if (!result || result.error) {
            // Even if Mapbox fails, create a record so user can manually classify
            const record = {
              location_name: loc.townCode,
              location_type: 'unknown',
              display_as: loc.townCode,
              parent_town: null,
              is_verified: false,
              show_in_export: true,
              mapbox_place: null,
              mapbox_locality: null,
              mapbox_neighborhood: null,
              mapbox_district: null,
              mapbox_region: null,
              mapbox_context: null,
              source_addresses: loc.sampleAddresses.slice(0, 5),
              updated_at: new Date().toISOString(),
            };

            const { error: upsertError } = await supabase
              .from('address_town_classifications')
              .upsert(record, { onConflict: 'location_name' });

            if (!upsertError) successCount++;
            else failCount++;
            continue;
          }

          // Determine classification from Mapbox context
          const place = result.place || null;
          const locality = result.locality || null;
          const neighborhood = result.neighborhood || null;

          let locationType = 'unknown';
          let displayAs = loc.townCode;
          let parentTown: string | null = null;

          if (place) {
            // Mapbox found a place (town/city)
            displayAs = place;
            if ((locality || neighborhood) && (locality || neighborhood) !== place) {
              // There's a suburb AND a different city - this address is in a suburb
              locationType = 'suburb';
              parentTown = place;
            } else {
              locationType = 'town';
            }
          } else if (locality || neighborhood) {
            locationType = 'town';
            displayAs = locality || neighborhood || loc.townCode;
          }

          const record = {
            location_name: loc.townCode,
            location_type: locationType,
            display_as: displayAs,
            parent_town: parentTown,
            is_verified: false,
            show_in_export: true,
            mapbox_place: place,
            mapbox_locality: locality,
            mapbox_neighborhood: neighborhood,
            mapbox_district: result.district || null,
            mapbox_region: result.region || null,
            mapbox_context: result.full_context || null,
            source_addresses: loc.sampleAddresses.slice(0, 5),
            updated_at: new Date().toISOString(),
          };

          const { error: upsertError } = await supabase
            .from('address_town_classifications')
            .upsert(record, { onConflict: 'location_name' });

          if (upsertError) {
            console.error('Upsert error for', loc.townCode, upsertError);
            failCount++;
          } else {
            successCount++;
          }
        }
      } catch (err: any) {
        console.error('Batch classify error:', err);
        failCount += batch.length;
      }
    }

    setClassifying(false);
    onClassificationsChanged();

    toast({
      title: 'Classification Complete',
      description: `${successCount} classified${failCount > 0 ? `, ${failCount} failed` : ''}`,
      variant: failCount > 0 && successCount === 0 ? 'destructive' : 'default',
    });
  }, [allLocations, onClassificationsChanged]);

  // ─── Classify Single Location (Manual) ──────────────────────────
  const handleSetType = useCallback(async (
    loc: typeof allLocations[0],
    newType: 'town' | 'suburb' | 'unknown'
  ) => {
    const key = loc.townCode;
    setSaving(key);
    try {
      const cls = loc.classification;

      if (cls) {
        // Update existing
        const updates: any = {
          location_type: newType,
          is_verified: true,
          updated_at: new Date().toISOString(),
        };
        if (newType === 'town') {
          updates.parent_town = null;
          if (!cls.display_as || cls.display_as === cls.location_name) {
            updates.display_as = cls.mapbox_place || cls.location_name;
          }
        }
        if (newType === 'suburb' && !cls.parent_town) {
          updates.parent_town = cls.mapbox_place || '';
        }

        const { error } = await supabase
          .from('address_town_classifications')
          .update(updates)
          .eq('id', cls.id);
        if (error) throw error;
      } else {
        // Create new
        const record = {
          location_name: loc.townCode,
          location_type: newType,
          display_as: loc.townCode,
          parent_town: null,
          is_verified: true,
          show_in_export: true,
          source_addresses: loc.sampleAddresses.slice(0, 5),
          updated_at: new Date().toISOString(),
        };
        const { error } = await supabase
          .from('address_town_classifications')
          .upsert(record, { onConflict: 'location_name' });
        if (error) throw error;
      }

      onClassificationsChanged();
    } catch (err: any) {
      toast({ title: 'Update Failed', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  }, [onClassificationsChanged]);

  // ─── Save Edit (Display As / Parent Town) ───────────────────────
  const handleSaveEdit = useCallback(async (cls: TownClassification) => {
    setSaving(cls.id);
    try {
      const { error } = await supabase
        .from('address_town_classifications')
        .update({
          display_as: editDisplayAs.trim() || cls.location_name,
          parent_town: editParentTown.trim() || null,
          is_verified: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', cls.id);
      if (error) throw error;
      setEditingId(null);
      onClassificationsChanged();
    } catch (err: any) {
      toast({ title: 'Update Failed', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  }, [editDisplayAs, editParentTown, onClassificationsChanged]);

  // ─── Delete Classification ──────────────────────────────────────
  const handleDelete = useCallback(async (cls: TownClassification) => {
    setSaving(cls.id);
    try {
      const { error } = await supabase
        .from('address_town_classifications')
        .delete()
        .eq('id', cls.id);
      if (error) throw error;
      onClassificationsChanged();
      toast({ title: 'Deleted', description: `Classification for "${cls.location_name}" removed.` });
    } catch (err: any) {
      toast({ title: 'Delete Failed', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  }, [onClassificationsChanged]);

  // ─── Add Manual Classification ──────────────────────────────────
  const handleAddManual = useCallback(async () => {
    if (!manualName.trim()) return;
    setSaving('manual');
    try {
      const record = {
        location_name: manualName.trim(),
        location_type: manualType,
        display_as: manualDisplayAs.trim() || manualName.trim(),
        parent_town: manualType === 'suburb' ? (manualParentTown.trim() || null) : null,
        is_verified: true,
        show_in_export: true,
        source_addresses: [],
        updated_at: new Date().toISOString(),
      };
      const { error } = await supabase
        .from('address_town_classifications')
        .upsert(record, { onConflict: 'location_name' });
      if (error) throw error;
      setAddingManual(false);
      setManualName('');
      setManualDisplayAs('');
      setManualParentTown('');
      setManualType('town');
      onClassificationsChanged();
      toast({ title: 'Added', description: `Classification for "${manualName.trim()}" saved.` });
    } catch (err: any) {
      toast({ title: 'Add Failed', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  }, [manualName, manualType, manualDisplayAs, manualParentTown, onClassificationsChanged]);

  // ─── Start editing ──────────────────────────────────────────────
  const startEdit = useCallback((cls: TownClassification) => {
    setEditingId(cls.id);
    setEditDisplayAs(cls.display_as || cls.location_name);
    setEditParentTown(cls.parent_town || '');
  }, []);

  const typeIcon = (type: string | null | undefined) => {
    switch (type) {
      case 'town': return <Building2 className="w-3.5 h-3.5 text-blue-400" />;
      case 'suburb': return <Home className="w-3.5 h-3.5 text-orange-400" />;
      default: return <HelpCircle className="w-3.5 h-3.5 text-gray-500" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-emerald-400" />
            Town Classifier
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Classify town codes from your TaxiCaller addresses. Suburbs will show the parent town in exports.
          </p>
        </DialogHeader>

        {/* Stats Bar */}
        <div className="flex flex-wrap items-center gap-3 text-xs border-b border-border/40 pb-3">
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">Locations:</span>
            <span className="font-semibold">{stats.total}</span>
          </div>
          <div className="h-4 w-px bg-border" />
          <button
            onClick={() => setFilterType(filterType === 'town' ? 'all' : 'town')}
            className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors ${filterType === 'town' ? 'bg-blue-900/30 text-blue-400' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <Building2 className="w-3 h-3" />
            <span>{stats.towns} towns</span>
          </button>
          <button
            onClick={() => setFilterType(filterType === 'suburb' ? 'all' : 'suburb')}
            className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors ${filterType === 'suburb' ? 'bg-orange-900/30 text-orange-400' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <Home className="w-3 h-3" />
            <span>{stats.suburbs} suburbs</span>
          </button>
          <button
            onClick={() => setFilterType(filterType === 'unclassified' ? 'all' : 'unclassified')}
            className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors ${filterType === 'unclassified' ? 'bg-amber-900/30 text-amber-400' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <HelpCircle className="w-3 h-3" />
            <span>{stats.unclassified} unclassified</span>
          </button>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAddingManual(!addingManual)}
            className="h-7 text-xs border-border text-muted-foreground hover:text-foreground"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Manual
          </Button>
          <Button
            size="sm"
            onClick={handleClassifyAll}
            disabled={classifying || stats.unclassified === 0}
            className="h-7 text-xs golden-button"
          >
            {classifying ? (
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
            ) : (
              <Scan className="w-3 h-3 mr-1" />
            )}
            {classifying ? 'Classifying...' : `Classify ${stats.unclassified} via Mapbox`}
          </Button>
        </div>

        {/* Add Manual Form */}
        {addingManual && (
          <div className="border border-emerald-700/30 bg-emerald-900/10 rounded-md p-3 space-y-2">
            <p className="text-xs font-medium text-emerald-400">Add Manual Classification</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-muted-foreground">Town Code / Name</label>
                <Input
                  value={manualName}
                  onChange={e => setManualName(e.target.value)}
                  placeholder="e.g. PN, West, Takaro"
                  className="dark-input text-xs h-7"
                />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Type</label>
                <div className="flex gap-1 mt-0.5">
                  <button
                    onClick={() => setManualType('town')}
                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded text-xs border transition-colors ${
                      manualType === 'town'
                        ? 'bg-blue-900/40 text-blue-400 border-blue-700/40'
                        : 'border-border text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Building2 className="w-3 h-3" /> Town
                  </button>
                  <button
                    onClick={() => setManualType('suburb')}
                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded text-xs border transition-colors ${
                      manualType === 'suburb'
                        ? 'bg-orange-900/40 text-orange-400 border-orange-700/40'
                        : 'border-border text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Home className="w-3 h-3" /> Suburb
                  </button>
                </div>
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Display As (full town name)</label>
                <Input
                  value={manualDisplayAs}
                  onChange={e => setManualDisplayAs(e.target.value)}
                  placeholder="e.g. Palmerston North"
                  className="dark-input text-xs h-7"
                />
              </div>
              {manualType === 'suburb' && (
                <div>
                  <label className="text-[10px] text-muted-foreground">Parent Town</label>
                  <Input
                    value={manualParentTown}
                    onChange={e => setManualParentTown(e.target.value)}
                    placeholder="e.g. Palmerston North"
                    className="dark-input text-xs h-7"
                  />
                </div>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => setAddingManual(false)} className="h-6 text-xs">Cancel</Button>
              <Button
                size="sm"
                onClick={handleAddManual}
                disabled={!manualName.trim() || saving === 'manual'}
                className="h-6 text-xs golden-button"
              >
                {saving === 'manual' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Save
              </Button>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Search town codes, display names, zones..."
            className="dark-input text-xs h-8 pl-8"
          />
          {searchText && (
            <button onClick={() => setSearchText('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Locations List */}
        <div className="flex-1 overflow-y-auto min-h-0 space-y-1 pr-1">
          {filteredLocations.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <MapPin className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">
                {allLocations.length === 0
                  ? 'No addresses found in current data. Load data first.'
                  : 'No locations match the current filter.'}
              </p>
            </div>
          ) : (
            filteredLocations.map(loc => {
              const cls = loc.classification;
              const isSaving = saving === loc.townCode || saving === cls?.id;
              const isEditing = editingId === cls?.id;

              return (
                <div
                  key={loc.key}
                  className={`border rounded-md px-3 py-2 transition-colors ${
                    cls
                      ? cls.location_type === 'town'
                        ? 'border-blue-700/20 bg-blue-900/5'
                        : cls.location_type === 'suburb'
                          ? 'border-orange-700/20 bg-orange-900/5'
                          : 'border-border/50'
                      : 'border-amber-700/30 bg-amber-900/5'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {/* Type Icon */}
                    <div className="flex-shrink-0">
                      {cls ? typeIcon(cls.location_type) : <HelpCircle className="w-3.5 h-3.5 text-amber-400" />}
                    </div>

                    {/* Location Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold">{loc.townCode}</span>
                        {loc.zoneCodes.size > 0 && (
                          <span className="text-[10px] text-muted-foreground/60">
                            zones: {Array.from(loc.zoneCodes).sort().join(', ')}
                          </span>
                        )}
                        <span className="text-[10px] text-muted-foreground/60">{loc.count} uses</span>
                        {cls?.is_verified && (
                          <CheckCircle2 className="w-3 h-3 text-green-400/60 flex-shrink-0" />
                        )}
                      </div>

                      {/* Classification details */}
                      {cls && !isEditing && (
                        <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
                          <Badge className={`text-[9px] px-1.5 py-0 ${
                            cls.location_type === 'town'
                              ? 'bg-blue-900/30 text-blue-400 border-blue-700/40'
                              : cls.location_type === 'suburb'
                                ? 'bg-orange-900/30 text-orange-400 border-orange-700/40'
                                : 'bg-gray-800/40 text-gray-500 border-gray-700/40'
                          }`}>
                            {cls.location_type}
                          </Badge>
                          {cls.display_as && cls.display_as !== cls.location_name && (
                            <span>
                              Shows as: <span className="text-emerald-400 font-medium">{cls.display_as}</span>
                            </span>
                          )}
                          {cls.location_type === 'suburb' && cls.parent_town && (
                            <span>
                              Parent: <span className="text-blue-400 font-medium">{cls.parent_town}</span>
                            </span>
                          )}
                          {cls.mapbox_place && (
                            <span className="text-muted-foreground/40">
                              Mapbox: {cls.mapbox_place}
                              {cls.mapbox_locality ? ` / ${cls.mapbox_locality}` : ''}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Edit mode */}
                      {cls && isEditing && (
                        <div className="mt-1.5 space-y-1.5">
                          <div className="flex items-center gap-2">
                            <label className="text-[10px] text-muted-foreground w-20 flex-shrink-0">Display as:</label>
                            <Input
                              value={editDisplayAs}
                              onChange={e => setEditDisplayAs(e.target.value)}
                              className="dark-input text-xs h-6 flex-1"
                              placeholder="Full town name"
                            />
                          </div>
                          {cls.location_type === 'suburb' && (
                            <div className="flex items-center gap-2">
                              <label className="text-[10px] text-muted-foreground w-20 flex-shrink-0">Parent town:</label>
                              <Input
                                value={editParentTown}
                                onChange={e => setEditParentTown(e.target.value)}
                                className="dark-input text-xs h-6 flex-1"
                                placeholder="e.g. Palmerston North"
                              />
                            </div>
                          )}
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-5 text-[10px] px-2">Cancel</Button>
                            <Button size="sm" onClick={() => handleSaveEdit(cls)} disabled={isSaving} className="h-5 text-[10px] px-2 golden-button">
                              {isSaving ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : 'Save'}
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Not classified hint */}
                      {!cls && (
                        <div className="text-[10px] text-amber-400/60 mt-0.5">
                          Not classified — use the buttons to set as Town or Suburb, or click "Classify via Mapbox"
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        size="sm"
                        variant={cls?.location_type === 'town' ? 'default' : 'ghost'}
                        onClick={() => handleSetType(loc, 'town')}
                        disabled={isSaving}
                        className={`h-6 text-[10px] px-2 ${
                          cls?.location_type === 'town'
                            ? 'bg-blue-900/40 text-blue-400 border border-blue-700/40'
                            : 'text-muted-foreground hover:text-blue-400'
                        }`}
                      >
                        <Building2 className="w-3 h-3 mr-0.5" />
                        Town
                      </Button>
                      <Button
                        size="sm"
                        variant={cls?.location_type === 'suburb' ? 'default' : 'ghost'}
                        onClick={() => handleSetType(loc, 'suburb')}
                        disabled={isSaving}
                        className={`h-6 text-[10px] px-2 ${
                          cls?.location_type === 'suburb'
                            ? 'bg-orange-900/40 text-orange-400 border border-orange-700/40'
                            : 'text-muted-foreground hover:text-orange-400'
                        }`}
                      >
                        <Home className="w-3 h-3 mr-0.5" />
                        Suburb
                      </Button>
                      {cls && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEdit(cls)}
                            disabled={isSaving}
                            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(cls)}
                            disabled={isSaving}
                            className="h-6 w-6 p-0 text-muted-foreground hover:text-red-400"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                      {isSaving && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                    </div>
                  </div>

                  {/* Sample addresses */}
                  {loc.sampleAddresses.length > 0 && (
                    <div className="mt-1.5 pl-6">
                      <div className="text-[9px] text-muted-foreground/40 space-y-0.5">
                        {loc.sampleAddresses.slice(0, 2).map((addr, i) => (
                          <div key={i} className="truncate">{addr}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="pt-3 border-t border-border/40 flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Showing {filteredLocations.length} of {allLocations.length} locations
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={onClose} className="h-7 text-xs border-border">
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TownClassifier;
